import { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';

/** Where the admins get the 80% warning (lib/ai/budget.ts on the server). */
const WARN_AT = 80;

/**
 * This month's AI spend against the limit, as a real progress bar: a visible
 * track in both themes, a fill that grows to the current share, a tick where
 * the 80% warning fires, and the share and the money left spelled out.
 */
export function BudgetProgress({
  spendEur,
  budgetEur,
  format,
}: {
  spendEur: number;
  budgetEur: number;
  /** "51,41" — the screen's own euro formatting. */
  format: (value: number) => string;
}) {
  const { t } = useI18n();
  const ratio = budgetEur > 0 ? (spendEur / budgetEur) * 100 : 0;
  const pct = Math.min(100, Math.max(0, ratio));
  // Grow from empty on mount, then follow the value.
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(frame);
  }, [pct]);

  const color = ratio >= 100 ? 'bg-red-500' : ratio >= WARN_AT ? 'bg-amber-500' : 'bg-emerald-500';
  const left = budgetEur - spendEur;

  return (
    <div className="mt-3">
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label={t('aiBudget.progressLabel')}
        className="relative h-3 w-full overflow-hidden rounded-full bg-foreground/10 ring-1 ring-inset ring-foreground/10"
      >
        <div
          className={`h-full rounded-full ${color} transition-[width] duration-700 ease-out`}
          style={{ width: `${shown}%` }}
        />
        <div
          className="absolute inset-y-0 w-px bg-foreground/30"
          style={{ left: `${WARN_AT}%` }}
          title={t('aiBudget.warnTick')}
        />
      </div>
      <div className="mt-1.5 flex flex-wrap justify-between gap-2 text-xs text-gray-500">
        <span className="font-medium text-gray-700">
          {t('aiBudget.usedPct', { pct: Math.round(ratio) })}
        </span>
        <span>
          {left >= 0
            ? t('aiBudget.remaining', { amount: format(left) })
            : t('aiBudget.overBy', { amount: format(-left) })}
        </span>
      </div>
    </div>
  );
}
